/**
 * The plant's limits (Stage 2 of docs/plans/2026-09-27_ai-report-on-the-brief-engine.md): the process windows and the
 * scrap targets a process engineer sets, with the analysis's suggestions beside them. What is not words: where the
 * Limits page opens for an input, and each defect's recent history from the month briefs, which a target is set
 * against. Pure.
 */
import type { BriefDefect, BriefDriver, CadenceBrief, PlantBrief } from "@/lib/api/brief";
import type {
  LimitBasis,
  LimitBasisKind,
  ProcessWindow,
  ProcessWindowBatch,
  ProcessWindowParameter,
  ProcessWindowRowError,
  ProcessWindowWrite,
  ScrapTarget,
  ScrapTargetWrite,
} from "@/lib/api/limits";
import { pct, periodLabel } from "@/lib/brief/words";
import { SECOND_PROBE_WORDS, SHORT_FORMS, probeOf } from "@/lib/brief/param-words";
import { formatDay } from "@/lib/plant-days";
import { rowsToCsv } from "@/lib/reports/export-csv";
import { normalise } from "@/lib/units/convert";
import { shownFor, shownForFile, typedToStored } from "@/lib/units/display";
import type { DisplayPrefs } from "@/lib/units/prefs";
import { conditionValueText } from "./cadence-words";
import { SHARE_UNIT } from "./words";

/**
 * The Limits page at one input's window: its condition key, and its grade where the evidence was tested per grade
 * ("grade:G-2" opens grade G-2's window).
 */
export function limitsHref(condition: string, stratum?: string): string {
  const grade = stratum?.startsWith("grade:") ? stratum.slice("grade:".length) : "";
  return `/app/twin/limits?condition=${encodeURIComponent(condition)}${grade ? `&grade=${encodeURIComponent(grade)}` : ""}`;
}

/** One plant month of a defect (or of the total scrap): its pieces over the month's castings. */
export interface LimitsMonth {
  month: string;
  pieces: number;
  cast: number;
  rate: number | null;
}

/** Three months back to back, pooled: the first and last month and the pooled rate. */
export interface LimitsQuarter {
  from: string;
  to: string;
  rate: number;
}

/**
 * A defect's recent history, which a target is set against: its months (oldest first), the best three months back to
 * back (the lowest pooled rate), and the latest three months pooled (recent). A quarter needs three months that cast
 * anything; none otherwise.
 */
export interface LimitsHistory {
  key: string;
  label: string;
  months: LimitsMonth[];
  best: LimitsQuarter | null;
  recent: LimitsQuarter | null;
}

/** The pooled rate of months: their pieces over their castings; null with nothing cast. */
function pooled(months: readonly LimitsMonth[]): number | null {
  const cast = months.reduce((n, m) => n + m.cast, 0);
  return cast > 0 ? months.reduce((n, m) => n + m.pieces, 0) / cast : null;
}

function quarterOf(months: readonly LimitsMonth[]): LimitsQuarter | null {
  const rate = pooled(months);
  return rate === null ? null : { from: months[0].month, to: months[months.length - 1].month, rate };
}

/**
 * Each defect's history, and the total scrap's (key "total", label "All scrap"), from the month briefs: a defect's
 * pieces in a month are its Pareto row's (none booked is 0 pieces), over the month's castings. Months that cast nothing
 * are left out. The defects come most pieces first over the months read, the total first of all.
 */
export function limitsHistoryOf(briefs: readonly CadenceBrief[]): LimitsHistory[] {
  const months = briefs
    .filter((b) => b.cadence === "month" && b.scrap && b.scrap.cast > 0)
    .sort((a, b) => a.period_key.localeCompare(b.period_key));
  const labels = new Map<string, string>();
  const totals = new Map<string, number>();
  for (const b of months) {
    for (const r of b.scrap!.pareto) {
      labels.set(r.defect, r.label);
      totals.set(r.defect, (totals.get(r.defect) ?? 0) + r.pieces);
    }
  }
  const historyOf = (key: string, label: string, piecesOf: (b: CadenceBrief) => number): LimitsHistory => {
    const series = months.map((b) => {
      const pieces = piecesOf(b);
      const cast = b.scrap!.cast;
      return { month: b.period_key, pieces, cast, rate: cast > 0 ? pieces / cast : null };
    });
    let best: LimitsQuarter | null = null;
    for (let i = 0; i + 3 <= series.length; i++) {
      const q = quarterOf(series.slice(i, i + 3));
      if (q && (best === null || q.rate < best.rate)) best = q;
    }
    return { key, label, months: series, best, recent: series.length >= 3 ? quarterOf(series.slice(-3)) : null };
  };
  const out = [historyOf("total", "All scrap", (b) => b.scrap!.scrap)];
  const defects = [...totals.keys()].sort((a, b) => totals.get(b)! - totals.get(a)! || a.localeCompare(b));
  for (const d of defects) {
    out.push(historyOf(d, labels.get(d) ?? d, (b) => b.scrap!.pareto.find((r) => r.defect === d)?.pieces ?? 0));
  }
  return out;
}

/** "Apr to Jun 2026, 7.8%" (a quarter's months and pooled rate). */
export function quarterText(q: LimitsQuarter): string {
  const [fy, fm] = q.from.split("-");
  const [ty] = q.to.split("-");
  const from = fy === ty ? periodLabel(`${fy}-${fm}`).split(" ")[0] : periodLabel(q.from);
  return `${from} to ${periodLabel(q.to)}, ${pct(q.rate)}`;
}

/**
 * The best three months of the months a row shows, saying they are those (the 2026-10-24 sweep, item 13.8): the
 * overall brief's best three months look back over the plant's whole history, so the two can differ.
 */
export function bestQuarterSentence(q: LimitsQuarter, months: number): string {
  return `Best three months back to back of the ${months} months above: ${quarterText(q)}. The overall brief looks back over all the plant's history.`;
}

/** A window's ends in words, open where it has one: "0.035 % to 0.05 %", "1356 °C and above", "up to 4.4". */
export function windowText(w: { lo?: number; hi?: number }, unit: string, prefs?: DisplayPrefs): string {
  const lo = w.lo === undefined ? "" : conditionValueText(w.lo, unit, prefs);
  const hi = w.hi === undefined ? "" : conditionValueText(w.hi, unit, prefs);
  if (lo && hi) return `${lo} to ${hi}`;
  if (lo) return `${lo} and above`;
  if (hi) return `up to ${hi}`;
  return "";
}

/** A number as the form shows it: at most four decimals, no trailing zeros. */
export function formNumber(v: number | undefined): string {
  return v === undefined ? "" : String(Number(v.toFixed(4)));
}

/**
 * The end a form saves (#1089 review, MEDIUM 1): the exact value it started from while its field still shows it (the
 * field shows at most four decimals; start is shown times scale, 100 for a target's percent), else the value typed. A
 * suggested end is a quantile of the lots' values and rarely has only four decimals: saved as shown, it would no
 * longer be the suggestion's.
 */
export function formEndOf(text: string, start: number | undefined, typed: number | undefined, scale = 1): number | undefined {
  return start !== undefined && text.trim() === formNumber(start * scale) ? start : typed;
}

/**
 * What a window's save says it was set from (#1089 review, MEDIUM 1 and LOW 2): the tag's band taken (the platform
 * reads the band itself); once a window is set, nothing (the platform keeps the window's basis where the ends are
 * kept); else the suggestion the form started from, as it stood where the ends are its own, typed from it where they
 * were changed. Nothing where there is no suggestion: typed. Pure.
 */
export function windowSaveBasisOf(s: {
  suggestion?: { lo?: number; hi?: number; resultId: string };
  ends: { lo?: number; hi?: number };
  fromBand?: boolean;
  windowSet?: boolean;
}): LimitBasis | undefined {
  if (s.fromBand) return { kind: "tag_band" };
  if (s.windowSet || !s.suggestion) return undefined;
  const kept = s.ends.lo === s.suggestion.lo && s.ends.hi === s.suggestion.hi;
  return { kind: kept ? "suggestion" : "typed", result_id: s.suggestion.resultId };
}

/** A number the person typed; undefined when blank, NaN when it is not a number. */
function typed(text: string): number | undefined {
  const t = text.trim();
  if (t === "") return undefined;
  const v = Number(t);
  return Number.isFinite(v) ? v : Number.NaN;
}

/** A window's form read: its ends, or why it cannot be saved. */
export type WindowForm = { ok: true; lo?: number; hi?: number } | { ok: false; error: string };

/** Reads a window's form: an end or two, each a number, the low below the high (the server checks the same). */
export function windowFormOf(form: { lo: string; hi: string }): WindowForm {
  const lo = typed(form.lo);
  const hi = typed(form.hi);
  if (lo === undefined && hi === undefined) return { ok: false, error: "Give a low end, a high end or both." };
  if (lo !== undefined && Number.isNaN(lo)) return { ok: false, error: "The low end is not a number." };
  if (hi !== undefined && Number.isNaN(hi)) return { ok: false, error: "The high end is not a number." };
  if (lo !== undefined && hi !== undefined && !(lo < hi)) return { ok: false, error: "The low end must be below the high end." };
  return { ok: true, ...(lo === undefined ? {} : { lo }), ...(hi === undefined ? {} : { hi }) };
}

/** A target's form read: its rate as a fraction, or why it cannot be saved. */
export type TargetForm = { ok: true; rate: number } | { ok: false; error: string };

/** Reads a target typed as a percentage of castings ("4.5" is 4.5%): from 0% to 100%. */
export function targetFormOf(text: string): TargetForm {
  const v = typed(text);
  if (v === undefined || Number.isNaN(v)) return { ok: false, error: "Give the target as a percentage of castings." };
  if (v < 0 || v > 100) return { ok: false, error: "A target is from 0% to 100% of castings." };
  return { ok: true, rate: v / 100 };
}

/**
 * A driver whose range is a window the plant can set on its input: one with a range, other than a day's share within a
 * setpoint band, whose range is of that share and never of the tag's values (#1089 review, LOW 3). The Limits page
 * lists these, and a brief's card links them to it.
 */
export function limitsDriver(driver: BriefDriver): boolean {
  return Boolean(driver.range) && driver.test !== "setpoint";
}

/** One defect's evidence on an input: its card and the driver with the range its fifths suggest. */
export interface LimitsEvidence {
  defect: BriefDefect;
  driver: BriefDriver;
}

/**
 * One input the plant can set a window for, in one grade or every grade (grade ""): its words and unit, the evidence
 * with a suggested range from each defect (strongest first), and the window the plant set, where it set one.
 */
export interface LimitsRow {
  condition: string;
  grade: string;
  label: string;
  unit: string;
  evidence: LimitsEvidence[];
  window?: ProcessWindow;
  /** The parameter as the plant records it: its stations, spread, and whether the briefs count it. */
  parameter?: ProcessWindowParameter;
}

const STRENGTH: Record<string, number> = { supported: 0, suggestive: 1, not_supported: 2 };

/**
 * The elements a plant keys its chemistry by, by symbol, in words with the symbol kept (the 2026-10-24 sweep, item
 * 13.2): "Ce" alone reads like CE, carbon equivalent. A key is read as an element only in the symbol's own case
 * ("Ce", "Pb", "C"), or in lower case where the two letters name nothing else ("mn", "pb").
 */
const ELEMENTS: Readonly<Record<string, string>> = {
  Al: "Aluminium", B: "Boron", Bi: "Bismuth", C: "Carbon", Ca: "Calcium", Ce: "Cerium", Co: "Cobalt", Cr: "Chromium",
  Cu: "Copper", La: "Lanthanum", Mg: "Magnesium", Mn: "Manganese", Mo: "Molybdenum", N: "Nitrogen", Nb: "Niobium",
  Ni: "Nickel", P: "Phosphorus", Pb: "Lead", S: "Sulphur", Sb: "Antimony", Si: "Silicon", Sn: "Tin", Te: "Tellurium",
  Ti: "Titanium", V: "Vanadium", W: "Tungsten", Zn: "Zinc", Zr: "Zirconium",
};

/** Two-letter symbols a key in lower case may name: none of them is also a word or another short form. */
const LOWER_ELEMENTS = new Set(["al", "bi", "cr", "cu", "mg", "mn", "mo", "nb", "ni", "pb", "sb", "si", "sn", "te", "ti", "zn", "zr"]);

/** An element symbol as written, from a key's word: "mg" is "Mg"; undefined for a word that is none. */
function symbolOf(word: string): string | undefined {
  if (ELEMENTS[word] !== undefined) return word;
  if (LOWER_ELEMENTS.has(word)) return `${word[0].toUpperCase()}${word[1]}`;
  return undefined;
}

/**
 * A condition key in words where the brief gives none (the 2026-10-24 sweep, item 13.2): an element alone by its name
 * with its symbol ("Ce" reads "Cerium (Ce)"), an element inside a key by its symbol ("residual_mg" reads "Residual Mg"),
 * a short form spelled out ("loi" reads "Loss on ignition"), else the key's words with a capital. A unit the key carries
 * at its end ("residual_mg(%)", "Al (%)") is left off: the row's heading writes its unit beside it. A second probe's
 * key reads as the name it shares and "(second probe)", never its tag's id (#1696).
 */
export function conditionWords(key: string): string {
  const probe = probeOf(key);
  if (probe.probe) return conditionWords(probe.key) + SECOND_PROBE_WORDS; // a second probe's (#1696)
  const bare = key.replace(/\s*\([^()]*\)\s*$/, "").trim() || key.trim();
  const alone = symbolOf(bare);
  if (alone !== undefined) return `${ELEMENTS[alone]} (${alone})`;
  const words = bare
    .split(/[_.\s]+/)
    .filter(Boolean)
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (SHORT_FORMS[lower] !== undefined) return SHORT_FORMS[lower];
      // A symbol inside a key ("residual_mg"); the first word is any word ("C" alone was read above).
      if (i > 0 && LOWER_ELEMENTS.has(lower)) return `${lower[0].toUpperCase()}${lower[1]}`;
      return w;
    });
  const w = words.join(" ");
  return w ? `${w[0].toUpperCase()}${w.slice(1)}` : key;
}

/**
 * A driver's label without the machine it names ("sand moisture on the Sand Mixer" reads "sand moisture"): the label
 * the engine writes is the parameter, then " on " and the machine (machineAt). A label with no machine is as it is.
 */
export function parameterOfLabel(label: string): string {
  const m = /^(.*?) on (?:the )?[A-Z0-9][^]*$/.exec(label);
  return m && m[1].trim() ? m[1].trim() : label;
}

const upperFirst = (s: string) => (s ? `${s[0].toUpperCase()}${s.slice(1)}` : s);

/**
 * The name the page keys a row on, from the platform's list: a name the plant records a parameter under (its
 * `recorded`, the priors' aliases the platform folds) is the parameter's own; any other name is as it is. The platform
 * keys a window's save the same way, so a row and the window it shows are one (#1087 review).
 */
export function limitsFoldOf(parameters: readonly ProcessWindowParameter[]): (name: string) => string {
  const to = new Map<string, string>();
  for (const p of parameters) for (const r of p.recorded ?? []) to.set(r, p.condition);
  return (name) => to.get(name) ?? name;
}

/**
 * The inputs the Limits page lists: every driver of the overall brief whose fifths suggest a window on its input (or
 * show none: limitsDriver), one row per condition and grade; every window the plant set, under its row or on its own; and every parameter the plant
 * records (the platform's list), whose rows it goes with, or a row of its own for every grade. Each is keyed on the
 * parameter's own name (limitsFoldOf). By condition, then grade.
 */
export function limitsRowsOf(
  brief: PlantBrief | null | undefined,
  windows: readonly ProcessWindow[],
  parameters: readonly ProcessWindowParameter[] = [],
): LimitsRow[] {
  const fold = limitsFoldOf(parameters);
  const rows = new Map<string, LimitsRow>();
  const rowOf = (condition: string, grade: string, label: string, unit: string): LimitsRow => {
    const k = `${condition}|${grade}`;
    let r = rows.get(k);
    if (!r) {
      r = { condition, grade, label, unit, evidence: [] };
      rows.set(k, r);
    }
    return r;
  };
  for (const defect of brief?.defects ?? []) {
    for (const driver of defect.drivers) {
      if (!limitsDriver(driver)) continue;
      // A share of the day in a setpoint band (brief/v19, its unit SHARE_UNIT) is no parameter the plant sets a window
      // on: its range belongs to the brief, never to the row of the parameter it tracks.
      if (driver.condition.unit === SHARE_UNIT) continue;
      const grade = driver.stratum?.startsWith("grade:") ? driver.stratum.slice("grade:".length) : "";
      rowOf(fold(driver.condition.key), grade, driver.condition.label, driver.condition.unit).evidence.push({ defect, driver });
    }
  }

  // The brief's words for each parameter a driver names, its machine left out: a row is the parameter, whose window
  // holds on every machine that reads it (the 2026-10-24 sweep, items 13.2 and 13.3).
  const words = new Map<string, string>();
  for (const defect of brief?.defects ?? []) {
    for (const d of defect.drivers) {
      const k = fold(d.condition.key);
      if (!words.has(k)) words.set(k, upperFirst(parameterOfLabel(d.condition.label)));
    }
  }
  const labelOf = (condition: string) => words.get(condition) ?? conditionWords(condition);
  // A row whose evidence names two machines or more is headed by the parameter alone: each suggestion names its own.
  for (const r of rows.values()) {
    if (new Set(r.evidence.map((e) => e.driver.condition.label)).size > 1) r.label = labelOf(r.condition);
  }
  for (const w of windows) {
    const condition = fold(w.condition);
    const r = rowOf(condition, w.grade ?? "", labelOf(condition), w.unit);
    r.window = w;
    if (!r.unit) r.unit = w.unit;
  }
  // A parameter keeps a row for every grade, where the plant sets its window for them all.
  for (const p of parameters) {
    if (!rows.has(`${p.condition}|`)) rowOf(p.condition, "", labelOf(p.condition), p.unit);
  }
  const byCondition = new Map(parameters.map((p) => [p.condition, p]));
  for (const r of rows.values()) {
    const p = byCondition.get(r.condition);
    if (!p) continue;
    r.parameter = p;
    if (!r.unit) r.unit = p.unit;
  }
  const out = [...rows.values()];
  for (const r of out) {
    r.evidence.sort(
      (a, b) =>
        (STRENGTH[a.driver.verdict] ?? 9) - (STRENGTH[b.driver.verdict] ?? 9) || a.defect.defect.localeCompare(b.defect.defect),
    );
  }
  return out.sort((a, b) => a.condition.localeCompare(b.condition) || a.grade.localeCompare(b.grade));
}

/** Where a row sits on the page: the analysis's suggestion, its test with none, a window alone, or any other. */
export type LimitsGroup = "suggested" | "tested" | "set" | "other";

/** The page's groups of rows, in order, each with its heading and what it holds. */
export const LIMITS_GROUPS: readonly { group: LimitsGroup; title: string; lead: string }[] = [
  {
    group: "suggested",
    title: "The analysis suggests a range",
    lead: "Inputs the analysis tied to a defect, where the plant's own data suggest a range to run in.",
  },
  {
    group: "tested",
    title: "Tested, no range suggested",
    lead: "Inputs the analysis tied to a defect, where the data do not show a better range.",
  },
  {
    group: "set",
    title: "Windows the plant set",
    lead: "Parameters the analysis does not test, with a window the plant set.",
  },
  {
    group: "other",
    title: "Every other parameter the plant records",
    lead: "The rest of what the plant records, each with its recent spread. Set a window on any of them.",
  },
];

/** A row's group: a suggested range first, then a test with none, then a window the plant set, then the rest. */
export function limitsGroupOf(row: LimitsRow): LimitsGroup {
  if (row.evidence.some((e) => e.driver.range?.suggested)) return "suggested";
  if (row.evidence.length > 0) return "tested";
  if (row.window) return "set";
  return "other";
}

/**
 * Whether a row answers a search: its name, words, unit, grade, stations (their names and ids) or the names it is
 * recorded under.
 */
export function limitsRowMatches(row: LimitsRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const p = row.parameter;
  return [row.condition, row.label, row.unit, row.grade, ...(p?.stations ?? []), ...(p?.station_names ?? []), ...(p?.recorded ?? [])].some(
    (s) => s.toLowerCase().includes(q),
  );
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

/**
 * "Last 180 days, 50 ladle readings over 25 heats: the middle 90% ran 0.031 % to 0.055 %, median 0.042 %.": the spread
 * of a parameter's readings, where it has some. A parameter read per heat or per ladle, where the process data starts
 * inside those days (since, from spreadSinceOf), says so: "Since 5 Jun 2031, when the process data starts, ..." (the
 * 2026-10-24 sweep, item 13.5: never "Last 180 days" over 80 days of data).
 */
export function spreadSentence(p: ProcessWindowParameter, days: number, prefs?: DisplayPrefs, since?: string): string | null {
  if (!p.spread || p.readings <= 0) return null;
  const v = (x: number) => conditionValueText(x, p.unit, prefs);
  const what = p.grain === "ladle" ? `${plural(p.readings, "ladle reading")} over ${plural(p.heats, "heat")}` : plural(p.heats, "heat");
  const heatwise = p.grain === "ladle" || p.grain === "heat";
  const when = since && heatwise ? `Since ${formatDay(since)}, when the process data starts` : `Last ${days} days`;
  return `${when}, ${what}: the middle 90% ran ${v(p.spread.p05)} to ${v(p.spread.p95)}, median ${v(p.spread.median)}.`;
}

/**
 * The process data's first plant day where it falls inside the last `days` days to `today` (both plant days), so the
 * spread is said from it; undefined where the data starts before them, or its start is not known.
 */
export function spreadSinceOf(processFrom: string | undefined, days: number, today: string): string | undefined {
  const from = processFrom?.slice(0, 10);
  if (!from || !/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(today)) return undefined;
  const start = Date.parse(`${today}T00:00:00Z`) - days * 86_400_000;
  return Date.parse(`${from}T00:00:00Z`) > start ? from : undefined;
}

/** What the page says when the plant recorded more names per shift or per mould than the platform's list holds. */
export function namesCutSentence(days: number): string {
  return `The plant recorded more than 500 names per shift or per mould in the last ${days} days: the first 500 are listed.`;
}

/**
 * Whether the briefs count a parameter against the plant's window (its ladles, or its heats where it is read once per
 * heat: PR5), and why not where they do not.
 */
export function countedSentence(p: ProcessWindowParameter, windowSet = true): string {
  // With no window of the plant's yet, the count is what the briefs would do once one is set (the 2026-10-24 sweep,
  // item 13.6): never "count against the plant's window" beside "No window set".
  if (p.counted) {
    const what = p.grain === "heat" ? "heats" : "ladles";
    return windowSet
      ? `The briefs count its ${what} against the plant's window.`
      : `The briefs would count its ${what} against the plant's window, once one is set.`;
  }
  const not = "the briefs do not count it against a window yet";
  switch (p.grain) {
    case "ladle":
      return `Recorded as ${(p.recorded ?? []).join(", ") || p.condition}: ${not}.`;
    case "heat":
      return `Read once per heat: ${not}.`;
    case "shift":
      return `Read once per shift: ${not}.`;
    case "mould":
      return `Read once per mould: ${not}.`;
    case "lot":
      return `A lot's condition, from its heats: ${not}.`;
    case "tag":
      return `A live tag: ${not}.`;
    default:
      return `The briefs do not count it against a window yet.`;
  }
}

/** A bound tag's band in words, whose it is said (a display band is a reference, never a specification). */
export function tagBandSentence(p: ProcessWindowParameter, prefs?: DisplayPrefs): string | null {
  const b = p.tag_band;
  const text = b ? windowText(b, p.unit, prefs) : "";
  if (!b || !text) return null;
  switch (b.spec_source) {
    case "customer":
      return `The tag's band is a customer specification: ${text}.`;
    case "internal":
      return `The tag's band is the plant's own control band: ${text}.`;
    default:
      return `The tag's display band: ${text}. A reference, not a specification.`;
  }
}

/** A tag band the plant may take as its window: its own control band or a customer's specification, never a display band. */
export function tagBandAdoptable(p: ProcessWindowParameter | undefined): p is ProcessWindowParameter & {
  tag_band: NonNullable<ProcessWindowParameter["tag_band"]>;
} {
  const b = p?.tag_band;
  return !!b && (b.spec_source === "internal" || b.spec_source === "customer") && (b.lo !== undefined || b.hi !== undefined);
}

/** A suggestion in words: "the analysis's suggestion of 12 Mar 2031, fifths 3 to 5". */
function suggestionWords(v: unknown): string {
  const s = (v ?? {}) as { run_done_at?: string; from_fifth?: number; to_fifth?: number };
  const on = typeof s.run_done_at === "string" ? ` of ${formatDay(s.run_done_at.slice(0, 10))}` : "";
  const fifths =
    s.from_fifth && s.to_fifth ? (s.from_fifth === s.to_fifth ? `, fifth ${s.from_fifth}` : `, fifths ${s.from_fifth} to ${s.to_fifth}`) : "";
  return `the analysis's suggestion${on}${fifths}`;
}

/** Whose a tag's band is, in words, and the tag it was read from where the platform named it. */
function bandWords(v: unknown): string {
  const b = (v ?? {}) as { spec_source?: string; tag?: string };
  const whose = b.spec_source === "customer" ? "the customer's specification on the tag" : "the tag's own control band";
  return typeof b.tag === "string" && b.tag ? `${whose} (${b.tag})` : whose;
}

/** A consultant's card in words: `the consultant's card "its title"`, as the platform copied it. */
function cardWords(v: unknown): string {
  const c = (v ?? {}) as { title?: unknown };
  return typeof c.title === "string" && c.title ? `the consultant's card "${c.title}"` : "the consultant's card";
}

/**
 * What a limit was set from, in words after who set it and when (migration 0193): ", from the analysis's suggestion of
 * 12 Mar 2031, fifths 3 to 5", ", typed, starting from ...", ", from the consultant's card "...", ", from
 * control-plan.csv, row 12", ", from the tag's own control band (its tag)". Empty where not recorded (a limit set before
 * it was).
 */
export function basisText(l: { basis?: LimitBasisKind; basis_detail?: Record<string, unknown> }): string {
  const d = l.basis_detail ?? {};
  switch (l.basis) {
    case "suggestion":
      return `, from ${suggestionWords(d.suggestion)}`;
    case "consultant":
      return `, from ${cardWords(d)}`;
    case "import":
      return `, from ${typeof d.file === "string" ? d.file : "a spreadsheet"}${typeof d.row === "number" ? `, row ${d.row}` : ""}`;
    case "tag_band":
      return `, from ${bandWords(d.band)}`;
    case "typed":
      if (d.started_from) return `, typed, starting from ${suggestionWords(d.started_from)}`;
      if (d.started_from_card) return `, typed, starting from ${cardWords(d.started_from_card)}`;
      if (d.started_from_band) return `, typed, starting from ${bandWords(d.started_from_band)}`;
      return ", typed";
    default:
      return "";
  }
}

/**
 * The suggestion a row's editor starts from, and the result it came from, for the basis a save records: the strongest
 * evidence's suggested range, where one suggests a range and names its result: the one the range was read from
 * (brief/v7), else, in an older brief, the driver's first.
 */
export function suggestionOf(row: LimitsRow): { lo?: number; hi?: number; resultId: string } | undefined {
  const e = row.evidence.find((x) => x.driver.range?.suggested);
  const r = e?.driver.range;
  const resultId = r?.result_id ?? e?.driver.result_ids?.[0];
  if (!r || !resultId || (r.lo === undefined && r.hi === undefined)) return undefined;
  return { ...(r.lo === undefined ? {} : { lo: r.lo }), ...(r.hi === undefined ? {} : { hi: r.hi }), resultId };
}

/** The columns of a spreadsheet of windows, in order. */
export const WINDOW_CSV_COLUMNS = ["parameter", "grade", "low", "high", "unit", "note", "reaction", "reaction_owner"] as const;

/**
 * The plant's open windows as a spreadsheet, the columns WINDOW_CSV_COLUMNS: a control plan to edit and import back.
 * Each row is in the org's shown unit (`prefs`) and its unit column says which: 1380 °C reads "2516" and "°F" for a US
 * org. The ends are the fewest figures that read back to the stored value, so an unedited file imported again changes
 * nothing. With no choice, or a unit the choice does not convert, a row is as stored.
 */
export function windowsToCsv(windows: readonly ProcessWindow[], prefs?: DisplayPrefs): string {
  return rowsToCsv([
    [...WINDOW_CSV_COLUMNS],
    ...windows.map((w) => {
      const s = shownFor(w.unit, prefs);
      const end = (v: number | undefined) => (v === undefined ? "" : shownForFile(v, w.unit, prefs));
      return [w.condition, w.grade ?? "", end(w.lo), end(w.hi), s.unit, w.note, w.reaction ?? "", w.reaction_owner ?? ""];
    }),
  ]);
}

/**
 * What a spreadsheet's rows are read against: the org's choice, and the unit each parameter's window is stored in
 * (a grade's window is stored as its parameter's). Without it a file's numbers are taken as they are.
 */
export interface WindowsUnits {
  prefs?: DisplayPrefs;
  storedUnitOf: (condition: string, grade: string) => string | undefined;
}

/** The unit each row of the page is stored in, found by the name a file gives a parameter (folded) and its grade. */
export function storedUnitLookup(
  rows: readonly LimitsRow[],
  fold: (name: string) => string,
): (condition: string, grade: string) => string | undefined {
  const by = new Map(rows.map((r) => [`${r.condition}|${r.grade}`, rowStoredUnit(r)]));
  return (condition, grade) => {
    const c = fold(condition);
    return by.get(`${c}|${grade}`) || by.get(`${c}|`) || undefined;
  };
}

/** A spreadsheet row read as a window, or why it cannot be one; row is the line of the file it starts on (the header's 1). */
export interface WindowCsvRow {
  row: number;
  condition: string;
  grade: string;
  lo?: number;
  hi?: number;
  unit: string;
  note: string;
  /** The reaction plan, where the file has the column (a file without it leaves the window's as it is). */
  reaction?: string;
  reaction_owner?: string;
  error?: string;
  /**
   * The ends as the platform stores them, where the file's are in another unit than the parameter's (a US org's file in
   * °F, a cell with no unit read as the shown unit): converted, with the stored unit. Absent where the row is sent as
   * the file has it.
   */
  convert?: { lo?: number; hi?: number; unit: string; from: string };
}

/**
 * The unit a previewed row's ends are read in: the row's own; for a cell left blank and read as the shown unit, that
 * unit, saying the file gave none (#1190 review MEDIUM 5). Blank where the row is sent as the file has it.
 */
export function csvRowUnit(row: WindowCsvRow): string {
  if (row.unit) return row.unit;
  return row.convert ? `${row.convert.from} (no unit given)` : "";
}

/** A previewed row's check, where nothing refused it: the ends it saves, in the stored unit, where they were converted. */
export function csvRowCheck(row: WindowCsvRow): string {
  if (!row.convert) return "ok";
  return `ok, saved as ${windowText(row.convert, row.convert.unit)}`;
}

/**
 * A row's ends moved to the unit the parameter is stored in, where the file gives them in the unit the page reads in:
 * the row's unit cell names it (a file exported from this page does), or, with no unit, it is the shown unit. A row
 * already in the stored unit, in any other unit, or of a parameter the page does not know, is sent as the file has it:
 * the platform checks its unit as it always did.
 */
function rowInStoredUnit(row: WindowCsvRow, units: WindowsUnits | undefined): WindowCsvRow {
  if (!units?.prefs || row.error) return row;
  const stored = units.storedUnitOf(row.condition, row.grade);
  if (!stored) return row;
  const shown = shownFor(stored, units.prefs).unit;
  const given = row.unit || shown;
  if (normalise(given) === normalise(stored) || normalise(given) !== normalise(shown)) return row;
  const lo = row.lo === undefined ? undefined : typedToStored(row.lo, given, stored);
  const hi = row.hi === undefined ? undefined : typedToStored(row.hi, given, stored);
  if (lo === null || hi === null) return { ...row, error: `The ends cannot be read in ${given}.` };
  return { ...row, convert: { ...(lo === undefined ? {} : { lo }), ...(hi === undefined ? {} : { hi }), unit: stored, from: given } };
}

/**
 * A CSV's records (RFC 4180: a quoted field may hold commas, doubled quotes and line breaks; a line break is CRLF, LF
 * or CR), each with the line of the file it starts on, the first line 1, as a spreadsheet numbers its rows. Pure.
 */
function csvRecords(text: string): { line: number; cells: string[] }[] {
  const out: { line: number; cells: string[] }[] = [];
  let cells: string[] = [];
  let cell = "";
  let quoted = false;
  let line = 1;
  let start = 1;
  const breakAt = (i: number) => (text[i] === "\r" ? (text[i + 1] === "\n" ? 2 : 1) : text[i] === "\n" ? 1 : 0);
  for (let i = 0; i < text.length; ) {
    const c = text[i];
    const n = breakAt(i);
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') {
        cell += '"';
        i += 2;
      } else if (c === '"') {
        quoted = false;
        i += 1;
      } else {
        // A line break inside a quoted field is the field's, and the next record starts a line further on.
        if (n) line += 1;
        cell += text.slice(i, i + (n || 1));
        i += n || 1;
      }
    } else if (c === '"' && cell === "") {
      quoted = true;
      i += 1;
    } else if (c === ",") {
      cells.push(cell);
      cell = "";
      i += 1;
    } else if (n) {
      cells.push(cell);
      out.push({ line: start, cells });
      cells = [];
      cell = "";
      line += 1;
      start = line;
      i += n;
    } else {
      cell += c;
      i += 1;
    }
  }
  if (cell !== "" || cells.length > 0 || quoted) {
    cells.push(cell);
    out.push({ line: start, cells });
  }
  return out;
}

/** A cell the export guarded (a leading apostrophe before what a spreadsheet would run as a formula), unguarded. */
function csvUnguarded(cell: string): string {
  return /^'[=+\-@\t\r]/.test(cell) ? cell.slice(1) : cell;
}

/**
 * Reads a spreadsheet of windows (CSV, a header row naming WINDOW_CSV_COLUMNS; "condition" is taken for "parameter",
 * "lo" and "hi" for "low" and "high"): each data row as a window, numbered by the line of the file it starts on (blank
 * lines skipped but counted: #1089 review, MEDIUM 3), a cell the export guarded read as it was written (MEDIUM 4),
 * checked as the page's form checks one, or why it cannot be. An error for a file that is no such spreadsheet.
 */
export function windowsFromCsv(text: string, units?: WindowsUnits): { rows: WindowCsvRow[]; error?: string } {
  const records = csvRecords(text.replace(/^\uFEFF/, "")).filter((r) => r.cells.some((c) => c.trim() !== ""));
  const [header, ...data] = records;
  const alias: Record<string, string> = { condition: "parameter", lo: "low", hi: "high" };
  const fields = (header?.cells ?? []).map((h) => {
    const f = h.trim().toLowerCase();
    return alias[f] ?? f;
  });
  if (!fields.includes("parameter")) {
    return { rows: [], error: "The file has no parameter column: its first row names the columns parameter, grade, low, high, unit, note." };
  }
  const rows = data.map((rec): WindowCsvRow => {
    const get = (col: string) => {
      const i = fields.indexOf(col);
      return i < 0 ? "" : csvUnguarded((rec.cells[i] ?? "").trim()).trim();
    };
    const out: WindowCsvRow = { row: rec.line, condition: get("parameter"), grade: get("grade"), unit: get("unit"), note: get("note") };
    // The reaction plan only where the file has its column: a file without one leaves the window's as it is.
    if (fields.includes("reaction")) out.reaction = get("reaction");
    if (fields.includes("reaction_owner")) out.reaction_owner = get("reaction_owner");
    if (!out.condition) return { ...out, error: "No parameter." };
    const form = windowFormOf({ lo: get("low"), hi: get("high") });
    if (!form.ok) return { ...out, error: form.error };
    return rowInStoredUnit(
      { ...out, ...(form.lo === undefined ? {} : { lo: form.lo }), ...(form.hi === undefined ? {} : { hi: form.hi }) },
      units,
    );
  });
  if (rows.length === 0) return { rows, error: "The file has no rows under its header." };
  return { rows };
}

/** The most rows the platform sets from one file (process_window_batch.go processWindowBatchMax). */
export const WINDOWS_BATCH_MAX = 500;

/** Why the platform would refuse a file whole (its name past 200 bytes, more rows than it takes), before it is sent. */
function windowsFileError(file: string, rows: number): string | undefined {
  if (new TextEncoder().encode(file.trim()).length > 200) {
    return "The file's name is longer than 200 characters: rename it and import it again.";
  }
  if (rows > WINDOWS_BATCH_MAX) {
    return `The file has ${rows} rows: the platform takes at most ${WINDOWS_BATCH_MAX} at once. Split the file.`;
  }
  return undefined;
}

/**
 * A file's bytes as text: UTF-8, a byte-order mark dropped; else why not (#1089 review, LOW 5). Excel's "CSV (Comma
 * delimited)" is the ANSI code page, whose "\u00B0" is no UTF-8: read as UTF-8 it would turn into a stand-in character.
 */
export function csvTextOf(buf: ArrayBuffer): { text: string } | { error: string } {
  try {
    return { text: new TextDecoder("utf-8", { fatal: true }).decode(buf) };
  } catch {
    return { error: 'The file is not UTF-8 text: save it from the spreadsheet as "CSV UTF-8" and import it again.' };
  }
}

/** The platform's reasons for refusing each row of a file, a row's reasons together (#1089 review, LOW 6). */
export function refusalsByRow(rows: readonly ProcessWindowRowError[]): Map<number, string> {
  const by = new Map<number, string>();
  for (const r of rows) by.set(r.row, by.has(r.row) ? `${by.get(r.row)}; ${r.error}` : r.error);
  return by;
}

/** A spreadsheet being imported: the file read, its rows, and the rows the platform refused when it was sent. */
export interface WindowsImport {
  file: string;
  rows: WindowCsvRow[];
  error?: string;
  /** The rows the platform refused when this file was sent (nothing was saved). */
  refused?: ProcessWindowRowError[];
}

/** What happens to an import: a file read, or one that cannot be; the file sent (of) refused or saved; the preview closed. */
export type WindowsImportEvent =
  | { type: "read"; file: string; text: string; units?: WindowsUnits }
  | { type: "unreadable"; file: string; error: string }
  | { type: "refused"; of: WindowsImport | null; rows: readonly ProcessWindowRowError[] }
  | { type: "saved"; of: WindowsImport | null }
  | { type: "cancel" };

/**
 * The import's next state: a file read replaces what was shown, its rows checked afresh (and the file against what the
 * platform takes at once) and no refusal carried over; a file that cannot be read shows why; the platform's refusal
 * marks the file sent, and its save closes it, only while that file is the one shown (a file read while another was
 * sent keeps its own preview); cancelled, nothing. Pure.
 */
export function windowsImportStep(state: WindowsImport | null, e: WindowsImportEvent): WindowsImport | null {
  switch (e.type) {
    case "read": {
      const read = windowsFromCsv(e.text, e.units);
      const error = read.error ?? windowsFileError(e.file, read.rows.length);
      return { file: e.file, rows: read.rows, ...(error ? { error } : {}) };
    }
    case "unreadable":
      return { file: e.file, rows: [], error: e.error };
    case "refused":
      return state !== null && state === e.of ? { ...state, refused: [...e.rows] } : state;
    case "saved":
      return state !== null && state === e.of ? null : state;
    case "cancel":
      return null;
  }
}

/**
 * The unit a row's window is STORED in, which a save names (the platform refuses another: unit_mismatch): the window's
 * own, else the parameter's, else the row's. Never the brief's alone: an org that reads in other units is served its
 * brief in them, and a row's `unit` is then the brief's.
 */
export function rowStoredUnit(row: LimitsRow): string {
  return row.window?.unit || row.parameter?.unit || row.unit;
}

/** The unit of the ends `windowStartOf` gives: a tag's band and a window are stored; a suggestion is the brief's. */
function startUnitOf(row: LimitsRow, from?: WindowFrom): string {
  if (from) return row.parameter?.unit || row.unit;
  if (row.window) return row.window.unit || row.unit;
  return row.unit;
}

/**
 * How a row's ends move between the unit they are held in, the shown unit the form is read in, and the stored unit a
 * save names. With no choice every move is the identity, so an org that is not asked to convert reads and sends exactly
 * what it always did.
 */
function endsOf(row: LimitsRow, from: WindowFrom | undefined, prefs: DisplayPrefs | undefined) {
  const stored = rowStoredUnit(row);
  const held = startUnitOf(row, from);
  const shown = shownFor(stored, prefs).unit;
  const move = (v: number | undefined, a: string, b: string): number | undefined => {
    if (v === undefined || !prefs) return v;
    return typedToStored(v, a, b) ?? v;
  };
  return {
    shown,
    stored,
    /** An end held in the start's unit, in the shown unit. */
    toShown: (v: number | undefined) => move(v, held, shown),
    /** An end held in the start's unit, in the stored unit. */
    heldToStored: (v: number | undefined) => move(v, held, stored),
    /** A suggestion's end (the brief's unit), in the stored unit. */
    suggestedToStored: (v: number | undefined) => move(v, row.unit, stored),
    /** An end typed in the shown unit, in the stored unit. */
    typedToStored: (v: number | undefined) => move(v, shown, stored),
  };
}

/**
 * The batch a previewed file sends: each row with its ends and unit as the platform stores them (a row read in the
 * shown unit goes in the stored unit: `convert`), the file's own otherwise. Pure.
 */
export function windowsBatchOf(preview: Pick<WindowsImport, "file" | "rows">): ProcessWindowBatch {
  return {
    file: preview.file,
    windows: preview.rows.map((r) => {
      const ends = r.convert ?? r;
      const unit = r.convert ? r.convert.unit : r.unit;
      return {
        row: r.row,
        condition: r.condition,
        ...(r.grade ? { grade: r.grade } : {}),
        ...(ends.lo === undefined ? {} : { lo: ends.lo }),
        ...(ends.hi === undefined ? {} : { hi: ends.hi }),
        ...(unit ? { unit } : {}),
        ...(r.note ? { note: r.note } : {}),
        ...(r.reaction === undefined ? {} : { reaction: r.reaction }),
        ...(r.reaction_owner === undefined ? {} : { reaction_owner: r.reaction_owner }),
      };
    }),
  };
}

/** A window's reaction plan in a line: "If it leaves the window: add Mg wire (the melt lead)."; "" for none. */
export function reactionText(w: Pick<ProcessWindow, "reaction" | "reaction_owner"> | undefined): string {
  const r = w?.reaction?.trim();
  if (!r) return "";
  const who = w?.reaction_owner?.trim();
  return `If it leaves the window: ${r.replace(/\.$/, "")}${who ? ` (${who})` : ""}.`;
}

/**
 * The reaction plan of a condition's window, for an excursion or a control plan row, by the one rule the server keeps
 * (platform-core projectWindowLookup): the window that holds the condition, under its name or one of its aliases, is
 * the grade's own where one is set, whether or not it names a reaction (the value was judged against it), else every
 * grade's; the first in the order served where two hold it. Undefined where none is set or it names no reaction. Pure.
 */
export function reactionsOf(windows: readonly ProcessWindow[]): (condition: string, grade: string) => ProcessWindow | undefined {
  return (condition, grade) => {
    const holds = (w: ProcessWindow) => w.condition === condition || (w.aliases ?? []).includes(condition);
    const of = (g: string) => windows.find((w) => holds(w) && (w.grade ?? "") === g);
    const w = (grade ? of(grade) : undefined) ?? of("");
    return w && (w.reaction ?? "").trim() !== "" ? w : undefined;
  };
}

/** The window to start from: the strongest evidence's suggested range, where one suggests a range. */
export function suggestedWindowOf(row: LimitsRow): { lo?: number; hi?: number } | undefined {
  const r = row.evidence.find((e) => e.driver.range?.suggested)?.driver.range;
  if (!r || (r.lo === undefined && r.hi === undefined)) return undefined;
  return { ...(r.lo === undefined ? {} : { lo: r.lo }), ...(r.hi === undefined ? {} : { hi: r.hi }) };
}

/** A tag's band taken as a window: its ends, and whose it is. */
export type WindowFrom = { lo?: number; hi?: number; spec_source?: string };

/** Where a row's window editor starts: a tag's band taken, else the window set, else the suggestion. */
export function windowStartOf(row: LimitsRow, from?: WindowFrom): { lo?: number; hi?: number } {
  return from ?? row.window ?? suggestionOf(row) ?? suggestedWindowOf(row) ?? {};
}

/**
 * What a row's window form shows when it opens, as text in the unit the form is read in (the org's shown unit): the
 * window the plant set, the suggestion, or a tag's band taken, each moved into that unit from the unit it is held in.
 */
export function windowFormStart(row: LimitsRow, from?: WindowFrom, prefs?: DisplayPrefs): { lo: string; hi: string } {
  const start = windowStartOf(row, from);
  const m = endsOf(row, from, prefs);
  return { lo: formNumber(m.toShown(start.lo)), hi: formNumber(m.toShown(start.hi)) };
}

/**
 * A row's window editor saved (#1089 review, MEDIUM 1): the write it sends, its ends the exact ones it started from
 * while their fields still show them (formEndOf) and what it was set from (windowSaveBasisOf); or why the form cannot
 * be sent. The form is read in the org's shown unit (`prefs`): an end typed there is sent in the unit the tag stores,
 * and an end the person did not touch is sent exactly as it was held, never a round trip through the shown number.
 * Pure.
 */
export function windowSaveOf(
  row: LimitsRow,
  form: { lo: string; hi: string; note: string; reaction?: string; reactionOwner?: string },
  from?: WindowFrom,
  prefs?: DisplayPrefs,
): { ok: true; write: ProcessWindowWrite } | { ok: false; error: string } {
  const f = windowFormOf(form);
  if (!f.ok) return f;
  const start = windowStartOf(row, from);
  const m = endsOf(row, from, prefs);
  const end = (text: string, held: number | undefined, typed: number | undefined) =>
    held !== undefined && text.trim() === formNumber(m.toShown(held)) ? m.heldToStored(held) : m.typedToStored(typed);
  const ends = { lo: end(form.lo, start.lo, f.lo), hi: end(form.hi, start.hi, f.hi) };
  const sug = suggestionOf(row);
  const basis = windowSaveBasisOf({
    suggestion: sug && { ...(sug.lo === undefined ? {} : { lo: m.suggestedToStored(sug.lo) }), ...(sug.hi === undefined ? {} : { hi: m.suggestedToStored(sug.hi) }), resultId: sug.resultId },
    ends,
    fromBand: Boolean(from?.spec_source),
    windowSet: Boolean(row.window),
  });
  return {
    ok: true,
    write: {
      condition: row.condition,
      ...(row.grade ? { grade: row.grade } : {}),
      ...(ends.lo === undefined ? {} : { lo: ends.lo }),
      ...(ends.hi === undefined ? {} : { hi: ends.hi }),
      unit: m.stored,
      note: form.note.trim(),
      ...(basis ? { basis } : {}),
      ...(form.reaction === undefined ? {} : { reaction: form.reaction.trim() }),
      ...(form.reactionOwner === undefined ? {} : { reaction_owner: form.reactionOwner.trim() }),
    },
  };
}

/**
 * A target's editor saved: the write it sends for the total (key "total") or a defect, its rate the target's exact one
 * while the field still shows it (a note edited keeps the target's basis: #1089 review, MEDIUM 2); or why the form
 * cannot be sent. Pure.
 */
export function targetSaveOf(
  key: string,
  target: ScrapTarget | undefined,
  form: { value: string; note: string },
): { ok: true; write: ScrapTargetWrite } | { ok: false; error: string } {
  const f = targetFormOf(form.value);
  if (!f.ok) return f;
  const which: Pick<ScrapTargetWrite, "kind" | "defect"> = key === "total" ? { kind: "total" } : { kind: "defect", defect: key };
  return { ok: true, write: { ...which, target_rate: formEndOf(form.value, target?.target_rate, f.rate, 100) ?? f.rate, note: form.note.trim() } };
}
